<template>
  <div class="flex w-full flex-col items-center">
    <div class="mb-6 w-full items-center justify-start">
      <span class="text-5xl font-medium">{{ __('Manage Item') }}</span>
    </div>

    <div class="flex h-full w-full sm:h-[650px]">
      <div
        class="flex h-full w-full justify-start rounded-lg bg-white p-6 shadow-md"
      >
        <div
          class="flex h-full w-full flex-col justify-between gap-11 lg:flex-row"
        >
          <!-- Product Image Section -->
          <div class="flex flex-col lg:w-[440px]">
            <div
              class="relative aspect-square overflow-hidden rounded-lg bg-gray-100"
            >
              <img
                :src="`/uploads/${selectedImage}`"
                :key="selectedImage"
                :alt="__('Media image')"
                class="h-full w-full object-cover"
              />

              <template v-if="data.product.images.length > 1">
                <div
                  @click="changeImage('back')"
                  class="absolute left-2 top-1/2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/50"
                >
                  <Icon
                    name="arrow_back"
                    type="filled"
                    class="p-2 text-sm text-white"
                  >
                  </Icon>
                </div>

                <div
                  @click="changeImage('next')"
                  class="absolute right-2 top-1/2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/50"
                >
                  <Icon
                    type="filled"
                    name="arrow_forward"
                    class="p-2 text-sm text-white"
                  >
                  </Icon>
                </div>
              </template>
              <div
                v-if="data.product.images.length > 1"
                class="absolute bottom-2 flex w-full flex-row items-center justify-center gap-x-2"
              >
                <div class="flex flex-row gap-x-2 rounded-xl bg-black/50 p-2">
                  <div
                    v-for="image in data.product.images"
                    class="h-1.5 w-1.5 rounded-full"
                    :class="
                      selectedImage === image.path ? 'bg-white' : 'bg-white/30'
                    "
                  ></div>
                </div>
              </div>
            </div>

            <!-- Thumbnail Images -->
            <div
              v-show="isNotMobile && data.product.images.length > 1"
              class="scrollbar-thin mt-4 flex gap-2 overflow-x-auto pb-2"
              style="scrollbar-gutter: stable"
              :class="data.product.images.length <= 5 ? 'justify-center' : ''"
            >
              <div
                v-for="(image, index) in data?.product?.images"
                :key="image.id"
                class="h-24 w-24 flex-shrink-0 cursor-pointer overflow-hidden rounded-md bg-gray-100 object-cover"
                @click="makePrimary(image)"
              >
                <img
                  :src="`/uploads/${image.path}`"
                  :key="image.id"
                  :alt="__('Media image')"
                  class="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          <!-- Product Details Section -->
          <div
            class="scrollbar-thin flex flex-col space-y-4 overflow-y-auto pr-4 lg:w-2/3"
            style="scrollbar-gutter: stable"
          >
            <!-- Product Title -->
            <h1 class="text-3xl font-semibold text-gray-900">
              {{ data.product.name }}
            </h1>

            <!-- Rating and Stock Info -->
            <div class="space-y-6">
              <div class="flex items-center gap-4">
                <template v-if="data.product.reviews_data.total_reviews > 0">
                  <span class="text-sm text-gray-600"
                    >{{ data.product.reviews_data.average_rating }}/5</span
                  >
                </template>

                <div class="flex">
                  <template
                    v-if="data.product.reviews_data.total_reviews !== 0"
                  >
                    <Icon
                      v-for="_ in data.product.reviews_data.average_rating
                        ? Math.floor(data.product.reviews_data.average_rating)
                        : 0"
                      name="star"
                      type="filled"
                      class="text-primary"
                    ></Icon>

                    <Icon
                      v-for="i in 5 -
                      (data.product.reviews_data.average_rating
                        ? Math.floor(data.product.reviews_data.average_rating)
                        : 0)"
                      name="star"
                      type="filled"
                      class="text-gray-300"
                    ></Icon>
                  </template>

                  <template v-else>
                    <Icon
                      v-for="i in 5"
                      name="star"
                      type="filled"
                      class="text-gray-300"
                    ></Icon>
                  </template>
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="14"
                  viewBox="0 0 15 14"
                  class="fill-primary"
                >
                  <path
                    d="M0.400024 14V1.4C0.400024 1.015 0.537108 0.685417 0.811274 0.41125C1.08544 0.137083 1.41502 0 1.80002 0H13C13.385 0 13.7146 0.137083 13.9888 0.41125C14.2629 0.685417 14.4 1.015 14.4 1.4V9.8C14.4 10.185 14.2629 10.5146 13.9888 10.7887C13.7146 11.0629 13.385 11.2 13 11.2H3.20002L0.400024 14ZM5.35252 8.575L7.40002 7.3325L9.44752 8.575L8.90502 6.2475L10.725 4.6725L8.32752 4.48L7.40002 2.275L6.47252 4.48L4.07502 4.6725L5.89502 6.2475L5.35252 8.575Z"
                  />
                </svg>

                <span
                  v-if="data.product.reviews_data.total_reviews !== 0"
                  class="text-sm text-gray-600"
                >
                  {{ __('Rated by') }}
                  {{ data.product.reviews_data.total_reviews }}
                  {{
                    data.product.reviews_data.total_reviews > 1
                      ? __('people')
                      : __('person')
                  }}.</span
                >
                <span v-else class="text-sm text-gray-600">
                  {{ __('This product has no reviews') }}
                </span>
              </div>

              <div
                class="flex flex-col items-start justify-center gap-4 text-sm sm:flex-row sm:items-center sm:justify-start"
              >
                <div class="flex items-center gap-2">
                  <Icon name="storefront" type="filled" class="text-primary" />
                  <div class="flex flex-row items-center gap-x-2">
                    <span class="text-zinc-800">
                      {{ __('All time stock ') }}
                    </span>
                    <transition
                      enter-active-class="transition-all duration-300 ease-out"
                      enter-from-class="translate-y-2 opacity-0"
                      enter-to-class="translate-y-0 opacity-100"
                      leave-active-class="transition-all duration-200 ease-in"
                      leave-from-class="translate-y-0 opacity-100"
                      leave-to-class="-translate-y-2 opacity-0"
                      mode="out-in"
                    >
                      <span
                        class="text-zinc-800"
                        :key="data.product.total_stock_all_time"
                      >
                        {{ data.product.total_stock_all_time }}</span
                      >
                    </transition>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <Icon
                    name="event_available"
                    type="filled"
                    class="text-primary"
                  ></Icon>
                  <div class="flex flex-row items-center gap-x-2">
                    <span class="text-zinc-800">
                      {{ __('Available Stock ') }}
                    </span>
                    <transition
                      enter-active-class="transition-all duration-300 ease-out"
                      enter-from-class="translate-y-2 opacity-0"
                      enter-to-class="translate-y-0 opacity-100"
                      leave-active-class="transition-all duration-200 ease-in"
                      leave-from-class="translate-y-0 opacity-100"
                      leave-to-class="-translate-y-2 opacity-0"
                      mode="out-in"
                    >
                      <span
                        class="text-zinc-800"
                        :key="data.product.total_available_stock"
                      >
                        {{ data.product.total_available_stock }}</span
                      >
                    </transition>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <Icon
                    name="visibility"
                    type="filled"
                    class="text-primary"
                  ></Icon>
                  <div class="flex flex-row items-center gap-x-2">
                    <span class="text-zinc-800">
                      {{
                        data.product.internal == 1 && data.product.external == 0
                          ? __('Internal')
                          : data.product.internal == 0 &&
                              data.product.external == 1
                            ? __('External')
                            : data.product.internal == 1 &&
                                data.product.external == 1
                              ? __('Visible to all')
                              : ''
                      }}</span
                    >
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-4 text-sm">
                <div class="flex items-center gap-1.5">
                  <InputToggle
                    id="is_active"
                    :value="Boolean(data.product.is_active)"
                    @update:value="
                      data.product.is_active = !data.product.is_active
                    "
                  />
                  <span class="font-medium">{{
                    data.product.is_active ? __('Listed') : __('Not Listed')
                  }}</span>
                </div>

                <button
                  @click="
                    redirectToPage(
                      'redesign.company.shop.products.edit',
                      data.product.id
                    )
                  "
                  class="btn btn-outline gap-2"
                >
                  <Icon name="edit" type="filled" class="text-primary"> </Icon>
                  <span class="text-primary">
                    {{ __('Edit Item') }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Out of Stock Warning -->
            <div
              v-if="hasOutOfStockSizes"
              class="flex items-start rounded-md py-4"
            >
              <div class="text-sm font-medium">
                <span
                  v-if="outOfStockSizes.filter((size) => size !== '').length"
                >
                  {{ __("We're out of the ") }}

                  [
                  {{ outOfStockSizes.join(', ') }}
                  ].</span
                >
                <span v-else> {{ __("We're out of stock.") }} </span>
                {{
                  __('Increase the stock quantity to make it available again.')
                }}
              </div>
            </div>

            <!-- Update Stock Section -->
            <div
              class="flex w-full flex-col items-center justify-start gap-y-4"
            >
              <span class="w-full text-2xl font-medium">
                {{ __('Update stock') }}
              </span>

              <div
                class="scrollbar-thin max-h-72 w-full overflow-y-auto rounded-lg border bg-white p-6"
                style="scrollbar-gutter: stable"
              >
                <div
                  v-for="variant in data.product.variants"
                  :key="variant.id"
                  class="flex items-stretch justify-between gap-x-2 pb-4"
                >
                  <div
                    v-if="variant.attributes.length !== 0"
                    class="w-full rounded-lg border px-4"
                    :class="`grid grid-cols-${variant.attributes.length} items-center`"
                  >
                    <span
                      v-for="attribute in variant.attributes"
                      class="text-center font-medium"
                      >{{ attribute.label }}</span
                    >
                  </div>

                  <div
                    v-else
                    class="w-full rounded-lg border px-4"
                    :class="`grid grid-cols-1 items-center gap-x-4`"
                  >
                    <span class="text-center font-medium">
                      {{ __('One Size') }}</span
                    >
                  </div>

                  <div
                    class="flex w-full items-center justify-between rounded-lg border px-4 py-2"
                  >
                    <button
                      @click="decreaseStock(variant.id)"
                      :disabled="variant.stock <= 0"
                    >
                      <Icon type="filled" name="remove" />
                    </button>

                    <input
                      v-if="variant.id in editingStock"
                      v-model="editingStock[variant.id]"
                      type="number"
                      min="0"
                      class="no-spinner w-16 rounded border px-2 py-1 text-center text-sm focus:ring-secondary"
                      @blur.prevent="saveStock(variant.id)"
                      @keydown.enter.prevent="saveStock(variant.id)"
                      @keydown.escape.prevent="cancelEdit(variant.id)"
                    />
                    <transition
                      v-else
                      enter-active-class="transition-all duration-300 ease-out"
                      enter-from-class="translate-y-2 opacity-0"
                      enter-to-class="translate-y-0 opacity-100"
                      leave-active-class="transition-all duration-200 ease-in"
                      leave-from-class="translate-y-0 opacity-100"
                      leave-to-class="-translate-y-2 opacity-0"
                      mode="out-in"
                    >
                      <span
                        :key="variant.stock"
                        class="inline-block cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                        @click="startEdit(variant.id, variant.stock)"
                      >
                        {{ variant.stock }}
                      </span>
                    </transition>

                    <button @click="increaseStock(variant.id)">
                      <Icon type="filled" name="add" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div
              class="mt-6 flex h-full w-full items-end justify-center gap-x-6 sm:justify-end"
            >
              <button
                @click="redirectToPage('redesign.company.shop.products.index')"
                class="btn btn-ghost hover-lift-no-shadow"
              >
                <span class="text-primary">
                  {{ __('Cancel') }}
                </span>
              </button>
              <button
                @click="handleSaveChanges"
                class="btn btn-fill hover-lift-no-shadow flex items-center justify-center gap-x-2"
                :disabled="isLoading"
              >
                <span class="text-white"> {{ __('Save Changes') }}</span>
                <Icon
                  v-if="isLoading"
                  class="animate-spin text-lg"
                  name="progress_activity"
                ></Icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick, onMounted } from 'vue';
  import { __ } from '@/helpers';
  import Icon from '@/components/global/Icon.vue';
  import InputToggle from '@/components/form/input-toggle/input-toggle.vue';
  import { useToast } from 'vue-toastification';

  const toast = useToast();

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });
  const errors = ref({});
  const data = ref(props.data);

  const isLoading = ref(false);
  onMounted(async () => {
    await nextTick();

    const storedMessage = sessionStorage.getItem('toast_message');
    if (storedMessage) {
      const { type, message } = JSON.parse(storedMessage);
      toast[type](message);
      sessionStorage.removeItem('toast_message');
    }
  });

  const lastClickTime = ref(0);

  const editingStock = ref({});

  const hasOutOfStockSizes = computed(() => {
    return data.value.product.variants.some((v) => v.stock === 0);
  });

  const outOfStockSizes = computed(() => {
    return data.value.product.variants
      .filter((v) => v.stock === 0)
      .map((v) => v.attributes.map((a) => a.label).join(', '));
  });

  const increaseStock = (variantId) => {
    const now = Date.now();
    if (now - lastClickTime.value < 300) return;

    lastClickTime.value = now;

    const variant = data.value.product.variants.find((v) => v.id === variantId);

    if (variant) {
      variant.stock += 1;
      data.value.product.total_available_stock += 1;
      data.value.product.total_stock_all_time += 1;
    }
  };

  const decreaseStock = (variantId) => {
    const now = Date.now();
    if (now - lastClickTime.value < 300) return;

    lastClickTime.value = now;
    const variant = data.value.product.variants.find((v) => v.id === variantId);
    if (variant && variant.stock > 0) {
      variant.stock -= 1;
      data.value.product.total_available_stock -= 1;
      data.value.product.total_stock_all_time -= 1;
    }
  };

  const handleSaveChanges = async () => {
    isLoading.value = true;
    try {
      const response = await axios.post(
        route('redesign.company.shop.products.update.stock'),
        data.value.product
      );
      sessionStorage.setItem(
        'toast_message',
        JSON.stringify({
          type: 'success',
          message: response.data.message
        })
      );

      window.location.href = response.data.redirect_url;
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
        errors.value = error.response.data.errors;
      } else {
        toast.error(__('Something went wrong'));
      }
    } finally {
      isLoading.value = false;
    }
  };

  const startEdit = (variantId, currentStock) => {
    editingStock.value = {};

    editingStock.value[variantId] = currentStock;

    nextTick(() => {
      const input = document.querySelector(`input[type="number"]`);
      if (input) {
        input.focus();
        input.select();
      }
    });
  };

  const saveStock = (variantId) => {
    const newStockValue = parseInt(editingStock.value[variantId]);
    if (!isNaN(newStockValue) && newStockValue >= 0) {
      const variant = data.value.product.variants.find(
        (v) => v.id === variantId
      );
      if (variant) {
        const oldStock = variant.stock;
        variant.stock = newStockValue;
        data.value.product.total_available_stock += newStockValue - oldStock;
        data.value.product.total_stock_all_time += newStockValue - oldStock;
      }
    }
    delete editingStock.value[variantId];
  };

  const cancelEdit = (variantId) => {
    delete editingStock.value[variantId];
  };

  const isNotMobile = computed(() => {
    return window.innerWidth >= 640;
  });

  const selectedImage = ref(
    data.value.product.images.find((item) => item.is_primary)?.path
  );
  function makePrimary(targetImage) {
    selectedImage.value = targetImage.path;
  }

  function changeImage(direction) {
    if (direction === 'next') {
      if (
        data.value.product.images.findIndex(
          (item) => item.path === selectedImage.value
        ) ===
        data.value.product.images.length - 1
      )
        return;

      selectedImage.value =
        data.value.product.images[
          data.value.product.images.findIndex(
            (item) => item.path === selectedImage.value
          ) + 1
        ].path;
    } else {
      if (
        data.value.product.images.findIndex(
          (item) => item.path === selectedImage.value
        ) === 0
      )
        return;

      selectedImage.value =
        data.value.product.images[
          data.value.product.images.findIndex(
            (item) => item.path === selectedImage.value
          ) - 1
        ].path;
    }
  }

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };
</script>
